import { ROLES_PERMISSION_LIST, DATE_FORMAT_WITH_TIME } from '../../../../constants/common';
import { HeadingMain } from '../../../../constants/style/common.styled';
import CustomCheckBox from '../../../../components/radio-button/customCheckBox';
import Heading from '../../../../components/typography';
import Search from '../../../../components/search/search';
import Tag from '../../../../components/lozenge/tags';
import { handleFilterPermissionList } from '../../../../utils/global';
import {
  CheckBoxFlex,
  FieldLevelPermissionsGroups,
  ListWrapper,
} from '../../system-roles/roles.styled';
import { PERMISSION_SEARCH_PLACEHOLDER } from './permissionMatrixCopy';
import {
  type PermissionMatrixFormProps,
  type GroupedPermissions,
  type PermissionChangeLog,
} from './types';
import moment from 'moment';

const getLatestChangeLogForGroup = (
  groupKey: string,
  permissionChangeLogs?: PermissionChangeLog[] | null
): PermissionChangeLog | undefined => {
  if (!permissionChangeLogs?.length) return undefined;
  const customerPrefix = `CustomerService.Customer.${groupKey}`;
  const vendorPrefix = `CustomerService.Vendor.${groupKey}`;
  const matching = permissionChangeLogs.filter(
    (log) =>
      log.permission === customerPrefix ||
      log.permission?.startsWith(`${customerPrefix}.`) ||
      log.permission === vendorPrefix ||
      log.permission?.startsWith(`${vendorPrefix}.`)
  );
  if (matching.length === 0) return undefined;
  return matching.reduce((latest, log) =>
    new Date(log.changedAt ?? 0) > new Date(latest.changedAt ?? 0) ? log : latest
  );
};

const getLatestFieldLevelChangeLogForGroup = (
  groupKey: string,
  permissionChangeLogs?: PermissionChangeLog[] | null
): PermissionChangeLog | undefined => {
  if (!permissionChangeLogs?.length) return undefined;
  const prefix = `CustomerService.Customer.FieldLevel.${groupKey}`;
  const matching = permissionChangeLogs.filter(
    (log) => log.permission === prefix || log.permission?.startsWith(`${prefix}.`)
  );
  if (matching.length === 0) return undefined;
  return matching.reduce((latest, log) =>
    new Date(log.changedAt ?? 0) > new Date(latest.changedAt ?? 0) ? log : latest
  );
};

interface PermissionMatrixFormInternalProps extends PermissionMatrixFormProps {
  handleCheckedAll: (
    key: string,
    setFieldValue: any,
    dataList: GroupedPermissions,
    wrapperName: string,
    isCheckedAll: boolean
  ) => void;
}

const PermissionMatrixForm = ({
  variant,
  modalStatus,
  values,
  errors,
  touched,
  setFieldValue,
  allFieldValues,
  filteredData,
  filteredFieldLevelData,
  nonFieldLevelPermissionsGroups,
  fieldLevelPermissionsGroups,
  filterNonFieldLevelPermission,
  filterFieldLevelPermission,
  manualSearch,
  onSearchChange,
  searchInputId = 'searchPermissionInput',
  handleCheckedAll,
  permissionChangeLogs,
}: PermissionMatrixFormInternalProps): JSX.Element => {
  const isBulk = variant === 'bulk';
  const isDisabled = modalStatus === 'View';
  const showAuditColumns = modalStatus !== 'Add' && !isBulk && !!permissionChangeLogs;

  const gridTemplateColumns = showAuditColumns
    ? '25% 1fr 1fr 1fr 1fr 1fr 1fr 12% 12%'
    : undefined;

  const labelColSpan = isBulk ? 'col-span-4' : 'col-span-5';
  const checkboxColSpan = isBulk ? 'col-span-7' : 'col-span-7';
  const selectColSpan = isBulk ? 'col-span-1' : '';

  return (
    <>
      <div id='searchSection' className='mb-6'>
        <Search
          placeholder={PERMISSION_SEARCH_PLACEHOLDER}
          searchValue={manualSearch}
          onSearch={(value: string) => {
            onSearchChange(value);
          }}
          id={searchInputId}
        />
      </div>

      <div className='table-wrapper'>
        {showAuditColumns ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns,
              gap: '0 8px',
              alignItems: 'center',
            }}>
            <div>
              <Tag variant='info' title='Module Level Permission List' id='moduleLevelPermissionList' />
            </div>
            <span className='roleSubTitle' style={{ textAlign: 'center', fontSize: '12px' }}>All</span>
            {ROLES_PERMISSION_LIST.map((col, i) => (
              <span key={`header-${i}`} className='roleSubTitle' style={{ textAlign: 'center', fontSize: '12px' }}>{col}</span>
            ))}
            <span className='roleSubTitle' style={{ fontSize: '12px' }}>Last Modified</span>
            <span className='roleSubTitle' style={{ fontSize: '12px' }}>Modified By</span>
          </div>
        ) : (
          <div className='grid grid-cols-12 gap-x-5'>
            {isBulk && <div className={selectColSpan}></div>}
            <div className={labelColSpan}>
              <Tag variant='info' title='Module Level Permission List' id='moduleLevelPermissionList' />
            </div>
            <div className={`grid grid-cols-12 ${checkboxColSpan}`}>
              <Heading
                variant='body-xs'
                title='All'
                id='allPermission'
                className='col-span-2 roleSubTitle'
              />
              {ROLES_PERMISSION_LIST.map((col, i) => (
                <Heading
                  key={`header-table-${i}`}
                  variant='body-xs'
                  title={col}
                  id={`permission${col}`}
                  className='col-span-2 roleSubTitle'
                />
              ))}
            </div>
          </div>
        )}

        {Object.keys(filteredData)
          .sort((a, b) => {
            const keyA = filteredData[a][0]?.key || '';
            const keyB = filteredData[b][0]?.key || '';
            const valueA = allFieldValues[keyA] || '';
            const valueB = allFieldValues[keyB] || '';
            return valueA.localeCompare(valueB);
          })
          .map((data: string) => {
            const allInfo = filteredData[data].filter(
              (item: any) => !(item.isDisabled as boolean) && item
            );

            const rolesInfo = filterNonFieldLevelPermission[data].filter(
              (item: any) => !(item.isDisabled as boolean) && item
            );

            const checkRoles = rolesInfo.map(
              (item: any) => !!(values.permissions[item.key.replaceAll('.', '-')] as boolean)
            );

            const labelKey = Boolean(allInfo) && allInfo.length > 0 && allInfo[0].key;

            if (!allFieldValues[labelKey]) return null;

            const isRowSelected = isBulk ? values?.selectedModulePermissions?.[data] : true;
            const groupChangeLog = showAuditColumns
              ? getLatestChangeLogForGroup(data, permissionChangeLogs)
              : undefined;

            if (showAuditColumns) {
              return (
                <CheckBoxFlex key={data}>
                  <ListWrapper
                    style={{
                      display: 'grid',
                      gridTemplateColumns,
                      gap: '0 8px',
                      alignItems: 'center',
                    }}>
                    <HeadingMain>
                      {allFieldValues ? allFieldValues[labelKey] : '-'}
                    </HeadingMain>
                    <div className='CheckboxCenterInRoles'>
                      <CustomCheckBox
                        name={`nonFieldPermissions${data}All`}
                        onChange={() => {
                          handleCheckedAll(
                            data,
                            setFieldValue,
                            nonFieldLevelPermissionsGroups,
                            'permissions',
                            checkRoles.every((v: boolean) => v)
                          );
                        }}
                        id={`nonFieldPermissions-${data}.All`}
                        errors={errors}
                        touched={touched}
                        disabled={isDisabled}
                        checked={checkRoles.every((v: boolean) => v)}
                      />
                    </div>
                    {ROLES_PERMISSION_LIST.map((item, index) => {
                      const itemData = handleFilterPermissionList(nonFieldLevelPermissionsGroups)[
                        `${data}`
                      ][index];
                      return (
                        <div key={`${data}-${item}-${index}`} className='CheckboxCenterInRoles'>
                          <CustomCheckBox
                            name={`permissions[${String(itemData.key.replaceAll('.', '-') ?? '')}]`}
                            onChange={(e: any) => {
                              if (
                                !(itemData.key.includes('View') === true) &&
                                e.target.checked === true
                              ) {
                                const newValue = itemData.key.replaceAll('.', '-').split('-');
                                const filterValue = newValue
                                  .filter(
                                    (filterItem: string, filterIndex: number) =>
                                      filterIndex !== newValue.length - 1 && filterItem
                                  )
                                  .join('-');
                                setFieldValue(`permissions[${String(filterValue ?? '')}]`, true);
                              }
                              setFieldValue(
                                `permissions[${String(itemData.key.replaceAll('.', '-') ?? '')}]`,
                                e.target.checked
                              );
                            }}
                            errors={errors}
                            touched={touched}
                            id={`${itemData.key.replaceAll('.', '-')}.${item}`}
                            arrValue={
                              item === itemData.text &&
                              values.permissions[itemData.key.replaceAll('.', '-')]
                            }
                            disabled={
                              isDisabled ||
                              !(itemData.key.length as boolean) ||
                              itemData.isDisabled
                            }
                          />
                        </div>
                      );
                    })}
                    <Heading
                      variant='body-xs'
                      title={
                        groupChangeLog?.changedAt
                          ? moment(groupChangeLog.changedAt).format(DATE_FORMAT_WITH_TIME)
                          : '-'
                      }
                    />
                    <Heading
                      variant='body-xs'
                      title={groupChangeLog?.changedByName ?? '-'}
                    />
                  </ListWrapper>
                </CheckBoxFlex>
              );
            }

            return (
              <ListWrapper key={data} className='grid items-center grid-cols-12 gap-x-5'>
                {isBulk && (
                  <div className='justify-center col-span-1 CheckboxCenterInRoles mb-8'>
                    <CustomCheckBox
                      name={`selectedModulePermissions[${data}]`}
                      id={`selectedModulePermissions-${data}.enablePermission`}
                      onChange={(e: any) => {
                        setFieldValue(`selectedModulePermissions[${data}]`, e.target.checked);
                        if (!e.target.checked) {
                          const rowPermissions = handleFilterPermissionList(
                            nonFieldLevelPermissionsGroups
                          )[data];
                          if (rowPermissions) {
                            rowPermissions.forEach((item: any) => {
                              if (item.key) {
                                setFieldValue(
                                  `permissions[${item.key.replaceAll('.', '-')}]`,
                                  false
                                );
                              }
                            });
                          }
                        }
                      }}
                      errors={errors}
                      touched={touched}
                      arrValue={values?.selectedModulePermissions?.[data]}
                    />
                  </div>
                )}
                <HeadingMain className={labelColSpan}>
                  {allFieldValues ? allFieldValues[labelKey] : '-'}
                </HeadingMain>
                <CheckBoxFlex className={`grid grid-cols-12 ${checkboxColSpan}`}>
                  <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                    <CustomCheckBox
                      name={`nonFieldPermissions${data}All`}
                      onChange={() => {
                        handleCheckedAll(
                          data,
                          setFieldValue,
                          nonFieldLevelPermissionsGroups,
                          'permissions',
                          checkRoles.every((v: boolean) => v)
                        );
                      }}
                      id={`nonFieldPermissions-${data}.All`}
                      errors={errors}
                      touched={touched}
                      disabled={isDisabled || (isBulk && !isRowSelected)}
                      checked={checkRoles.every((v: boolean) => v)}
                    />
                  </div>
                  {ROLES_PERMISSION_LIST.map((item, index) => {
                    const itemData = handleFilterPermissionList(nonFieldLevelPermissionsGroups)[
                      `${data}`
                    ][index];
                    return (
                      <div
                        key={`${data}-${item}-${index}`}
                        className='justify-center col-span-2 CheckboxCenterInRoles'>
                        <CustomCheckBox
                          name={`permissions[${String(itemData.key.replaceAll('.', '-') ?? '')}]`}
                          onChange={(e: any) => {
                            if (
                              !(itemData.key.includes('View') === true) &&
                              e.target.checked === true
                            ) {
                              const newValue = itemData.key.replaceAll('.', '-').split('-');
                              const filterValue = newValue
                                .filter(
                                  (filterItem: string, filterIndex: number) =>
                                    filterIndex !== newValue.length - 1 && filterItem
                                )
                                .join('-');
                              setFieldValue(`permissions[${String(filterValue ?? '')}]`, true);
                            }
                            setFieldValue(
                              `permissions[${String(itemData.key.replaceAll('.', '-') ?? '')}]`,
                              e.target.checked
                            );
                          }}
                          errors={errors}
                          touched={touched}
                          id={`${itemData.key.replaceAll('.', '-')}.${item}`}
                          arrValue={
                            item === itemData.text &&
                            values.permissions[itemData.key.replaceAll('.', '-')]
                          }
                          disabled={
                            isDisabled ||
                            (isBulk && !isRowSelected) ||
                            !(itemData.key.length as boolean) ||
                            itemData.isDisabled
                          }
                        />
                      </div>
                    );
                  })}
                </CheckBoxFlex>
              </ListWrapper>
            );
          })}
      </div>

      {Object.keys(filteredFieldLevelData).length > 0 && (
        <FieldLevelPermissionsGroups id='fieldLevelPermissionsSection'>
          {showAuditColumns ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns,
                gap: '0 8px',
                alignItems: 'center',
              }}
              className='roleTitle'>
              <div>
                <Tag variant='info' title='Field Level Permission' />
              </div>
            </div>
          ) : (
            <div className='grid grid-cols-12 gap-x-5 roleTitle'>
              {isBulk && <div className={selectColSpan}></div>}
              <div className={labelColSpan}>
                <Tag variant='info' title='Field Level Permission' />
              </div>
              <div className={`grid grid-cols-12 ${checkboxColSpan}`}>
                <Heading
                  variant='body-xs'
                  title='All'
                  id='allFieldPermission'
                  className='col-span-2 roleSubTitle'
                />
                {ROLES_PERMISSION_LIST.map((col, i) => (
                  <Heading
                    key={`field-header-${i}`}
                    variant='body-xs'
                    title={col}
                    id={`fieldPermission${col}`}
                    className='col-span-2 roleSubTitle'
                  />
                ))}
              </div>
            </div>
          )}
          {Object.keys(filteredFieldLevelData).map((data: string) => {
            const allInfo = filteredFieldLevelData[data]?.filter(
              (item: any) => !(item?.isDisabled as boolean) && item
            );

            const rolesInfo = (filterFieldLevelPermission[data] ?? []).filter(
              (item: any) => !(item?.isDisabled as boolean) && item
            );

            const checkRoles = rolesInfo.map(
              (item: any) =>
                !!(values?.fieldLevelPermissions[item?.key?.replaceAll('.', '-')] as boolean)
            );

            const labelKey = Boolean(allInfo) && allInfo.length > 0 && allInfo[0].key;
            const isRowSelected = isBulk ? values?.selectedFieldPermissions?.[data] : true;
            const fieldGroupChangeLog = showAuditColumns
              ? getLatestFieldLevelChangeLogForGroup(data, permissionChangeLogs)
              : undefined;

            if (showAuditColumns) {
              return (
                <CheckBoxFlex key={data}>
                  <ListWrapper
                    style={{
                      display: 'grid',
                      gridTemplateColumns,
                      gap: '0 8px',
                      alignItems: 'center',
                    }}
                    id={`fieldLevelPermissionRow${data}`}>
                    <HeadingMain>
                      {allFieldValues ? allFieldValues[labelKey] : '-'}
                    </HeadingMain>
                    <div className='CheckboxCenterInRoles'>
                      <CustomCheckBox
                        name={`fieldLevelPermissions${data}All`}
                        id={`fieldLevelPermissions-${data}.All`}
                        onChange={() => {
                          handleCheckedAll(
                            data,
                            setFieldValue,
                            fieldLevelPermissionsGroups,
                            'fieldLevelPermissions',
                            checkRoles.every((v: boolean) => v)
                          );
                        }}
                        errors={errors}
                        touched={touched}
                        disabled={isDisabled}
                        checked={checkRoles.every((v: boolean) => v)}
                      />
                    </div>
                    {ROLES_PERMISSION_LIST.map((item, index) => {
                      const itemData = handleFilterPermissionList(fieldLevelPermissionsGroups)[
                        `${data}`
                      ][index];
                      return (
                        <div key={`${data}-field-${item}-${index}`} className='CheckboxCenterInRoles'>
                          <CustomCheckBox
                            name={`fieldLevelPermissions[${String(
                              itemData.key.replaceAll('.', '-') ?? ''
                            )}]`}
                            id={`${itemData.key.replaceAll('.', '-')}.${item}`}
                            onChange={(e: any) => {
                              if (
                                !(itemData.key.includes('View') === true) &&
                                e.target.checked === true
                              ) {
                                const newValue = itemData.key.replaceAll('.', '-').split('-');
                                const filterValue = newValue
                                  .filter(
                                    (filterItem: string, filterIndex: number) =>
                                      filterIndex !== newValue.length - 1 && filterItem
                                  )
                                  .join('-');
                                setFieldValue(
                                  `fieldLevelPermissions[${String(filterValue ?? '')}]`,
                                  true
                                );
                              }
                              setFieldValue(
                                `fieldLevelPermissions[${String(
                                  itemData.key.replaceAll('.', '-') ?? ''
                                )}]`,
                                e.target.checked
                              );
                            }}
                            errors={errors}
                            touched={touched}
                            arrValue={
                              item === itemData.text &&
                              values.fieldLevelPermissions[itemData.key.replaceAll('.', '-')]
                            }
                            disabled={
                              isDisabled ||
                              !(itemData.key.length as boolean) ||
                              itemData.isDisabled
                            }
                          />
                        </div>
                      );
                    })}
                    <Heading
                      variant='body-xs'
                      title={
                        fieldGroupChangeLog?.changedAt
                          ? moment(fieldGroupChangeLog.changedAt).format(DATE_FORMAT_WITH_TIME)
                          : '-'
                      }
                    />
                    <Heading
                      variant='body-xs'
                      title={fieldGroupChangeLog?.changedByName ?? '-'}
                    />
                  </ListWrapper>
                </CheckBoxFlex>
              );
            }

            return (
              <ListWrapper
                key={data}
                className='grid items-center grid-cols-12 gap-x-5'
                id={`fieldLevelPermissionRow${data}`}>
                {isBulk && (
                  <div className='justify-center col-span-1 CheckboxCenterInRoles mb-8'>
                    <CustomCheckBox
                      name={`selectedFieldPermissions[${data}]`}
                      id={`selectedFieldPermissions-${data}.enablePermission`}
                      onChange={(e: any) => {
                        setFieldValue(`selectedFieldPermissions[${data}]`, e.target.checked);
                        if (!e.target.checked) {
                          const rowPermissions =
                            handleFilterPermissionList(fieldLevelPermissionsGroups)[data];
                          if (rowPermissions) {
                            rowPermissions.forEach((item: any) => {
                              if (item.key) {
                                setFieldValue(
                                  `fieldLevelPermissions[${item.key.replaceAll('.', '-')}]`,
                                  false
                                );
                              }
                            });
                          }
                        }
                      }}
                      errors={errors}
                      touched={touched}
                      arrValue={values?.selectedFieldPermissions?.[data]}
                    />
                  </div>
                )}
                <HeadingMain className={labelColSpan}>
                  {allFieldValues ? allFieldValues[labelKey] : '-'}
                </HeadingMain>
                <CheckBoxFlex className={`grid grid-cols-12 ${checkboxColSpan}`}>
                  <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                    <CustomCheckBox
                      name={`fieldLevelPermissions${data}All`}
                      id={`fieldLevelPermissions-${data}.All`}
                      onChange={() => {
                        handleCheckedAll(
                          data,
                          setFieldValue,
                          fieldLevelPermissionsGroups,
                          'fieldLevelPermissions',
                          checkRoles.every((v: boolean) => v)
                        );
                      }}
                      errors={errors}
                      touched={touched}
                      disabled={isDisabled || (isBulk && !isRowSelected)}
                      checked={checkRoles.every((v: boolean) => v)}
                    />
                  </div>
                  {ROLES_PERMISSION_LIST.map((item, index) => {
                    const itemData = handleFilterPermissionList(fieldLevelPermissionsGroups)[
                      `${data}`
                    ][index];
                    return (
                      <div
                        key={`${data}-field-${item}-${index}`}
                        className='justify-center col-span-2 CheckboxCenterInRoles'>
                        <CustomCheckBox
                          name={`fieldLevelPermissions[${String(
                            itemData.key.replaceAll('.', '-') ?? ''
                          )}]`}
                          id={`${itemData.key.replaceAll('.', '-')}.${item}`}
                          onChange={(e: any) => {
                            if (
                              !(itemData.key.includes('View') === true) &&
                              e.target.checked === true
                            ) {
                              const newValue = itemData.key.replaceAll('.', '-').split('-');
                              const filterValue = newValue
                                .filter(
                                  (filterItem: string, filterIndex: number) =>
                                    filterIndex !== newValue.length - 1 && filterItem
                                )
                                .join('-');
                              setFieldValue(
                                `fieldLevelPermissions[${String(filterValue ?? '')}]`,
                                true
                              );
                            }
                            setFieldValue(
                              `fieldLevelPermissions[${String(
                                itemData.key.replaceAll('.', '-') ?? ''
                              )}]`,
                              e.target.checked
                            );
                          }}
                          errors={errors}
                          touched={touched}
                          arrValue={
                            item === itemData.text &&
                            values.fieldLevelPermissions[itemData.key.replaceAll('.', '-')]
                          }
                          disabled={
                            isDisabled ||
                            (isBulk && !isRowSelected) ||
                            !(itemData.key.length as boolean) ||
                            itemData.isDisabled
                          }
                        />
                      </div>
                    );
                  })}
                </CheckBoxFlex>
              </ListWrapper>
            );
          })}
        </FieldLevelPermissionsGroups>
      )}
    </>
  );
};

export default PermissionMatrixForm;
